import type { ConverseUi, HitlQueueCard } from "../aidwops.types";
import useAidwopsStore from "../AidwopsStore";
import { ExpertQueueCard } from "./ExpertQueueCard";
import "./ExpertQueue.scss";

/**
 * What a queue command's answer shows under the assistant's text, for every `ui` the backend sends
 * except the queue itself (that one is `ExpertQueueView`): the item a command is about, the Jira text
 * that would be written, and a decision a command prepared. Draft decisions only prepare: the review
 * panel opens with the decision and reason filled in, and a person still confirms there.
 */
export function ChatUiBlock({ ui }: { ui: ConverseUi }) {
    const selectedItem = useAidwopsStore((state) => state.selectedItem);
    const setSelectedItem = useAidwopsStore((state) => state.setSelectedItem);

    const open = (item: HitlQueueCard) => setSelectedItem(item.id);

    switch (ui.type) {
        case "detail":
        case "why":
        case "created":
            return (
                <div className="chat-ui-block">
                    <div className="expert-queue-view-grid">
                        <ExpertQueueCard
                            item={ui.item}
                            rank={ui.item.rank ?? 1}
                            onOpen={open}
                            isSelected={ui.item.id === selectedItem}
                        />
                    </div>
                </div>
            );

        case "decision_draft":
            return (
                <div className="chat-ui-block">
                    <p className="expert-queue-item-detail-card-section-text">
                        Draft only: {ui.draft.decision} {ui.draft.id}. Nothing
                        is decided until you add a reason of at least{" "}
                        {ui.draft.reasonMinLength} characters, tick the
                        confirmation and confirm in the review panel.
                    </p>
                    {ui.draft.disagreesWithRecommendation && (
                        <div className="ac-alert">
                            <p className="ac-alert-title">
                                This goes against the AI recommendation.
                            </p>
                        </div>
                    )}
                    <div className="expert-queue-view-grid">
                        <ExpertQueueCard
                            item={ui.item}
                            rank={ui.item.rank ?? 1}
                            onOpen={open}
                            isSelected={ui.item.id === selectedItem}
                        />
                    </div>
                </div>
            );

        case "ticket_preview":
            return (
                <div className="chat-ui-block">
                    <div className="expert-queue-item-detail-card-section-label">
                        Jira preview &middot; {ui.id}
                    </div>
                    <pre className="expert-queue-item-detail-card-note">
                        {ui.preview.summary}
                        {"\n\n"}
                        {ui.preview.description}
                    </pre>
                </div>
            );

        default:
            // "queue" is drawn by ExpertQueueView; "text" and "none" have nothing to add to the answer.
            return null;
    }
}

export default ChatUiBlock;
